import React from "react";
import { Avatar, Badge } from "../../../ForBrands/ui/BrandUI";
import { usePhoneTheme } from "../../../ForBrands/useIsPhone";
import { EMPTY, classificationOf, initialsOf, playerMeta, poolTag, rowId } from "../allocationModel";
import AllocationCards from "./AllocationCards";
import { AddedTag, AllocatedPayout, Money, NilAllocated, POOLS, PlayerName, SportScore, VisibilityScore, eyeLabel, photoOf } from "./allocationCells";
import { EyeToggle } from "./allocationUi";
import { useContainerWidth } from "./useContainerWidth";

// Figma 03b · Team roster: the revenue share table and the NIL table side by
// side, one row per player in the same order, so the rows line up. Stacked
// (a column too narrow for both, or a caller passing `stacked`), the NIL
// table names its players itself. Nothing scrolls sideways: the tables
// measure their room and drop their least important columns instead. On
// phones (the phone theme, below 640px) the roster is one card per player
// instead (AllocationCards, Figma 2416:2527 option B), with the same cells.

const TD = "bm-td !py-0 h-14";
// Headers wrap onto a second line instead of spilling into the next column
// when a narrow tier leaves a column (or the group label) too little room.
const TH = "bm-th !whitespace-normal";
const DIM = "opacity-40";
const SKELETON_ROWS = 6;

// Widths in px. The Figma column at 1440 is 1196: the revenue share table,
// a 24px gap and the NIL table (802 + 24 + 370).
const FIGMA_WIDTH = 1196;
const GAP = 24; // gap-6
const FRAME_BORDER = 2;
const PLAYER_MIN = 200;

// Tiers, narrowest first. Full is the Figma table, lean drops the reference
// amounts (Team, All players, the NIL payout) and keeps each pool's
// Allocated, compact also drops Classification and tightens every cell.
const COMPACT = 0;
const LEAN = 1;
const FULL = 2;

// `tags` off: name and meta only (the stacked NIL table). The name, meta and
// pool tag truncate, with the full text on hover, so a row never grows. The
// Added tag never shrinks and comes before the pool tag, so its remove
// button shows even when the meta and the pool tag have no room left.
const PlayerCell = ({ row, onOpenProfile, onRemove, tags = true, avatar = true }) => {
  const tag = tags ? poolTag(row) : "";
  const added = tags && row.added;
  const meta = playerMeta(row);
  return (
    <div className="flex min-w-0 items-center gap-3">
      {avatar && <Avatar src={photoOf(row.avatar)} initials={initialsOf(row.playerName)} size={32} tone="outline" />}
      <div className="flex min-w-0 flex-col gap-[2px]">
        <PlayerName row={row} onOpenProfile={onOpenProfile} />
        {(meta || tag || added) && (
          <div className="flex min-w-0 items-center gap-2 overflow-hidden">
            {meta && (
              <span title={meta} className="truncate bm-body-s text-bm-muted">
                {meta}
              </span>
            )}
            {added && <AddedTag name={row.playerName} onRemove={() => onRemove(rowId(row))} />}
            {tag && (
              <span title={tag} className="flex min-w-0">
                <Badge tone="outline" dot="#edd200" className="min-w-0">
                  <span className="truncate">{tag}</span>
                </Badge>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const EyeCell = ({ row, out, pool, onToggle }) => (
  <div className="flex justify-center">
    <EyeToggle hidden={out} onToggle={() => onToggle(rowId(row), pool)} label={eyeLabel(row, pool, out)} />
  </div>
);

// One spec per column, in order. `width` is fixed and `narrow` replaces it in
// the compact tier; the player column has none and takes what is left. `from`
// is the narrowest tier still showing the column, `grouped` puts it under the
// group header, and a hidden player's cells dim except the eye and the `keep`
// cells, which stay white while the player still counts in NIL. Cells get
// (row, hidden, table context). The widths are the 10/2 layout: the eye 48,
// a plain amount 114 (its 100px content box holds $10,000,000.00), an
// Allocated box 126, so the NIL table fits beside the full revenue share
// table at the Figma width with the player at 200px or more.
const EYE = { key: "eye", label: "Show or hide", width: 48, narrow: 40, align: "center", cell: (r, out, t) => <EyeCell row={r} out={out} pool={t.pool} onToggle={t.onToggle} /> };

const REV_COLUMNS = [
  EYE,
  { key: "player", head: "Player name", keep: true, cell: (r, out, t) => <PlayerCell row={r} avatar={t.tier > COMPACT} onOpenProfile={t.onOpenProfile} onRemove={t.onRemove} /> },
  { key: "classification", head: "Classification", width: 96, from: LEAN, align: "center", keep: true, cell: (r) => <Badge tone="outline">{classificationOf(r) || EMPTY}</Badge> },
  { key: "score", head: "Sport score (out of 10)", width: 96, narrow: 72, align: "center", cell: (r) => <SportScore row={r} /> },
  { key: "team", head: "Team", width: 114, from: FULL, align: "money", grouped: true, cell: (r) => <Money value={r.payoutTeam} /> },
  { key: "all", head: "All players", width: 114, from: FULL, align: "money", grouped: true, cell: (r) => <Money value={r.payoutAllPlayers} /> },
  { key: "allocated", head: "Allocated", width: 126, narrow: 104, align: "box", grouped: true, cell: (r, out, t) => <AllocatedPayout row={r} disabled={out} onLock={t.onLock} /> },
];

// The score column is the player's CACHE Visibility score out of 10 ("-"
// without a CACHE account), one of the NIL model's inputs; the Sport score
// is out of 10 too, the scale every screen shows the pillars on. The NIL
// payout is the recommended split, a plain amount shown in the full tier
// like Team and All players; NIL Allocated, with the locks and the NILx
// minimums, locks like revenue share's.
const NIL_COLUMNS = [
  { ...EYE, grouped: true },
  { key: "player", head: "Player name", stackedOnly: true, grouped: true, cell: (r, out, t) => <PlayerCell row={r} tags={false} avatar={t.tier > COMPACT} onOpenProfile={t.onOpenProfile} /> },
  { key: "visibility", head: "Visibility score (out of 10)", width: 80, narrow: 72, align: "center", grouped: true, cell: (r) => <VisibilityScore row={r} /> },
  { key: "payout", head: "NIL payout", width: 114, from: FULL, align: "money", grouped: true, cell: (r) => <Money value={r.nilPayout} /> },
  { key: "allocated", head: "Allocated", width: 126, narrow: 104, align: "box", grouped: true, cell: (r, out, t) => <NilAllocated row={r} disabled={out} onLock={t.onLock} /> },
];

// Right-aligned amounts drop the 18px lead padding, dead space on their
// left, so a Figma 1196px table fits $10,000,000.00 in every money column.
// A boxed amount (Allocated) also keeps only 8px on its right, room the box
// needs for its border and padding.
const ALIGN = {
  center: { th: "!px-0 text-center", td: "!px-0 text-center" },
  money: { th: "text-right", td: "!pl-0 truncate text-right bm-body-m" },
  box: { th: "text-right", td: "!pl-0 !pr-2 text-right" },
};

const shownColumns = (columns, tier, stacked = false) => columns.filter((c) => tier >= (c.from ?? COMPACT) && (stacked || !c.stackedOnly));
const widthOf = (column, tier) => (tier === COMPACT && column.narrow) || column.width;
// The narrowest frame that holds these columns with the player at PLAYER_MIN.
const minWidth = (columns, tier) => columns.reduce((sum, c) => sum + (c.key === "player" ? PLAYER_MIN : widthOf(c, tier)), FRAME_BORDER);

// Side by side the NIL table has no player column, and its width is its
// columns' (370px).
const NIL_SIDE_WIDTH = minWidth(shownColumns(NIL_COLUMNS, FULL), FULL);
const SIDE_BY_SIDE_MIN = minWidth(shownColumns(REV_COLUMNS, FULL), FULL) + GAP + NIL_SIDE_WIDTH;

// Side by side only while the full revenue share table fits beside the NIL
// table. Stacked, both tables take the widest tier they both fit.
const layoutFor = (width, stacked) => {
  if (!stacked && width >= SIDE_BY_SIDE_MIN) return { sideBySide: true, tier: FULL };
  const fits = (tier) => [REV_COLUMNS, NIL_COLUMNS].every((columns) => width >= minWidth(shownColumns(columns, tier, true), tier));
  return { sideBySide: false, tier: [FULL, LEAN].find(fits) ?? COMPACT };
};

const fadeOf = (column, row, out) => {
  if (!out || column.key === "eye") return "";
  return column.keep && !row.hiddenNil ? "bg-white" : DIM;
};

const GroupHead = ({ lead, span, label }) => (
  <tr>
    {lead > 0 && <th colSpan={lead} className={`${TH} !h-9 !border-b-0`} aria-hidden="true" />}
    <th colSpan={span} className={`${TH} !h-9 !px-0 text-center`}>
      {label}
    </th>
  </tr>
);

const SkeletonRows = ({ cols }) =>
  Array.from({ length: SKELETON_ROWS }, (_, i) => (
    <tr key={i} aria-hidden="true">
      {Array.from({ length: cols }, (__, j) => (
        <td key={j} className={TD}>
          <span className="block h-3 w-full max-w-[120px] animate-pulse rounded-full bg-bm-mutedbg" />
        </td>
      ))}
    </tr>
  ));

// `table` carries the pool, the tier and the handlers the cells use.
const PoolTable = ({ columns, table, rows, loading, emptyText, className = "", style }) => {
  const { flag, group } = POOLS[table.pool];
  const fluid = columns.find((c) => c.key === "player") || columns[columns.length - 1];
  const grouped = columns.filter((c) => c.grouped).length;
  return (
    <div style={style} className={`min-w-0 overflow-hidden rounded-[12px] border border-bm-border bg-white ${className}`}>
      <table className={`w-full table-fixed ${table.tier === COMPACT ? "bm-table-dense" : ""}`}>
        <colgroup>
          {columns.map((c) => (
            <col key={c.key} style={c === fluid ? undefined : { width: widthOf(c, table.tier) }} />
          ))}
        </colgroup>
        <thead>
          <GroupHead lead={columns.length - grouped} span={grouped} label={group} />
          <tr>
            {columns.map((c) => (
              <th key={c.key} aria-label={c.label} className={`${TH} ${ALIGN[c.align]?.th || ""}`}>
                {c.head}
              </th>
            ))}
          </tr>
        </thead>
        <tbody aria-busy={loading} className={loading && rows.length ? "opacity-60" : ""}>
          {loading && !rows.length && <SkeletonRows cols={columns.length} />}
          {!loading && !rows.length && (
            <tr>
              <td colSpan={columns.length} className="bm-td py-10 text-center bm-body-m text-bm-muted">
                {emptyText}
              </td>
            </tr>
          )}
          {rows.map((r) => {
            const out = !!r[flag];
            return (
              <tr key={rowId(r)} className={out ? "bg-bm-subtle" : ""}>
                {columns.map((c) => (
                  <td key={c.key} className={`${TD} ${ALIGN[c.align]?.td || ""} ${fadeOf(c, r, out)}`}>
                    {c.cell(r, out, table)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const PoolTables = ({ rows, stacked = false, loading, emptyText, onToggle, onLock, onOpenProfile, onRemove }) => {
  const [ref, width] = useContainerWidth(FIGMA_WIDTH);
  const { sideBySide, tier } = layoutFor(width, stacked);
  const table = { tier, onToggle, onLock, onOpenProfile, onRemove };
  return (
    <div ref={ref} className={sideBySide ? "flex items-start gap-6" : "flex flex-col gap-6"}>
      <PoolTable
        columns={shownColumns(REV_COLUMNS, tier)}
        table={{ ...table, pool: "rs" }}
        rows={rows}
        loading={loading}
        emptyText={emptyText}
        className={sideBySide ? "flex-1" : ""}
      />
      <PoolTable
        columns={shownColumns(NIL_COLUMNS, tier, !sideBySide)}
        table={{ ...table, pool: "nil" }}
        rows={rows}
        loading={loading}
        className={sideBySide ? "shrink-0" : ""}
        style={sideBySide ? { width: NIL_SIDE_WIDTH } : undefined}
      />
    </div>
  );
};

// The switch follows the viewport, not the container (D-cs5), so a tablet
// or desktop column never flips to cards.
const AllocationTables = (props) => (usePhoneTheme() ? <AllocationCards {...props} /> : <PoolTables {...props} />);

export default AllocationTables;
